import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import moment from 'moment';
import useDate from 'src/hooks/useDate';
import Dialog from '@material-ui/core/Dialog';
import { images } from 'src/assets';
import { Button } from '@material-ui/core';
import { useRootAppSelector } from 'src/store/hooks';
import {
    selectedPassengersSelector,
    shiftCapsulesIdsSelector,
} from 'src/store/slices/manualOrders/manualOrdersSlice';
import LoadingButton from '@mui/lab/LoadingButton';
import * as Styles from '../Shiftcell.Style';
import ScopeChoice, { ShiftActionScope } from './ScopeChoice';

interface Props {
    isOpen: boolean;
    onClose: (val: boolean) => void;
    onDelete: () => void;
    startTime: string;
    endTime: string;
    date: string;
    showSpinner?: boolean;
    /** Only when other selected passengers have the same shift: delete for this passenger or for all of them */
    scope?: {
        value: ShiftActionScope;
        onChange: (scope: ShiftActionScope) => void;
        passengerName: string;
        allCount: number;
    };
}
const textStyle = {
    paddingTop: '5px',
};
const DeleteModalContent: FC<Props & { onExited: () => void }> = ({
    isOpen,
    onClose,
    onDelete,
    startTime,
    endTime,
    date,
    showSpinner,
    onExited,
    scope,
}) => {
    const { t } = useTranslation();
    const { fullDays } = useDate();
    const close = () => onClose(false);

    const shiftCapsulesForDeletionCount = useRootAppSelector(shiftCapsulesIdsSelector).length;

    return (
        <Dialog open={isOpen} onClose={close} TransitionProps={{ onExited }}>
            <Styles.ModalContainer style={scope ? { height: 'auto', width: '340px', minHeight: '203px' } : undefined}>
                <Styles.CircleModal>
                    <img width={27} height={27} src={images.grayTrash} alt="" />
                </Styles.CircleModal>
                <Styles.ModalBody>
                    <div style={{ fontSize: 20 }}>
                        {t('deleteShifts')}
                        {shiftCapsulesForDeletionCount ? <span> ({shiftCapsulesForDeletionCount})</span> : ''}
                    </div>
                    <div style={textStyle}>{`${startTime}-${endTime}`}</div>
                    <div style={textStyle}>{`${t('day')} ${fullDays[moment(date).day()]}, ${moment(
                        date
                    ).format('DD.MM')} `}</div>
                </Styles.ModalBody>
                {scope && (
                    <ScopeChoice
                        name={t('deleteForWhom')}
                        value={scope.value}
                        onChange={scope.onChange}
                        passengerName={scope.passengerName}
                        allCount={scope.allCount}
                    />
                )}
                <Styles.ModalAction>
                    <LoadingButton onClick={onDelete} color="primary" loading={showSpinner}>
                        {t('ok')}
                    </LoadingButton>
                    <Button onClick={close} style={{ color: 'gray' }}>
                        {t('cancel')}
                    </Button>
                </Styles.ModalAction>
            </Styles.ModalContainer>
        </Dialog>
    );
};

/**
 * Every shift in the grid has one of these. Closed, it rendered nothing visible but still ran its hooks and
 * date formatting on every render — 0.17 s of a 12-step scroll with 500 passengers (measured 2026-11-01).
 * It now mounts when opened and unmounts once the closing transition has ended, so the dialog looks the same.
 */
const DeleteModal: FC<Props> = (props) => {
    const { isOpen } = props;
    const [isMounted, setIsMounted] = useState(isOpen);

    useEffect(() => {
        if (isOpen) setIsMounted(true);
    }, [isOpen]);

    if (!isOpen && !isMounted) return null;

    return <DeleteModalContent {...props} onExited={() => setIsMounted(false)} />;
};

export default DeleteModal;
